import React from 'react';
import { Progress, Row, Col, Typography, Steps, Card } from 'antd';
import { CheckCircleOutlined, ClockCircleOutlined, UserOutlined } from '@ant-design/icons';
import { WorkflowAssignment } from '../../../types';

const { Text } = Typography;
const { Step } = Steps;

interface WorkflowProgressProps {
  visiteurs: WorkflowAssignment[];
  elaborateurs: WorkflowAssignment[];
  validateurs: WorkflowAssignment[];
}

const WorkflowProgress: React.FC<WorkflowProgressProps> = ({
  visiteurs,
  elaborateurs,
  validateurs,
}) => {
  const calculateProgress = () => {
    let totalSteps = 0;
    let completedSteps = 0;

    // Count visiteurs
    if (visiteurs.length > 0) {
      totalSteps += visiteurs.length;
      completedSteps += visiteurs.filter(v => v.date_completed).length;
    }

    // Count elaborateurs
    if (elaborateurs.length > 0) {
      totalSteps += elaborateurs.length;
      completedSteps += elaborateurs.filter(e => e.date_completed).length;
    }

    // Count validateurs
    if (validateurs.length > 0) {
      totalSteps += validateurs.length;
      completedSteps += validateurs.filter(v => v.date_completed).length;
    }

    return totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;
  };

  const getPhaseStatus = (assignments: WorkflowAssignment[]) => {
    if (assignments.length === 0) return 'not-started';
    
    const completed = assignments.filter(a => a.date_completed).length;
    const started = assignments.filter(a => a.date_started).length;
    
    if (completed === assignments.length) return 'completed';
    if (started > 0) return 'in-progress';
    return 'assigned';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'in-progress':
        return <ClockCircleOutlined style={{ color: '#faad14' }} />;
      case 'assigned':
        return <UserOutlined style={{ color: '#1890ff' }} />;
      default:
        return <ClockCircleOutlined style={{ color: '#d9d9d9' }} />;
    }
  };

  const getStatusText = (role: string, assignments: WorkflowAssignment[]) => {
    const status = getPhaseStatus(assignments);
    const completed = assignments.filter(a => a.date_completed).length;
    const total = assignments.length;

    switch (status) {
      case 'completed':
        return `${role} terminé (${completed}/${total})`;
      case 'in-progress':
        return `${role} en cours (${completed}/${total})`;
      case 'assigned':
        return `${role} assigné (${total})`;
      default:
        return `${role} en attente`;
    }
  };

  const getCurrentStep = () => {
    if (validateurs.some(v => v.date_completed)) return 3;
    if (validateurs.some(v => v.date_started)) return 2;
    if (elaborateurs.some(e => e.date_completed)) return 2;
    if (elaborateurs.some(e => e.date_started)) return 1;
    if (visiteurs.some(v => v.date_started)) return 0;
    return -1;
  };

  return (
    <Card size="small">
      
      
      <Steps current={getCurrentStep()} size="small" style={{ marginBottom: 16 }}>
        <Step
          title="Visite"
          description={`${visiteurs.length} visiteur(s)`}
          icon={getStatusIcon(getPhaseStatus(visiteurs))}
        />
        <Step
          title="Élaboration"
          description={`${elaborateurs.length} élaborateur(s)`}
          icon={getStatusIcon(getPhaseStatus(elaborateurs))}
        />
        <Step
          title="Validation"
          description={`${validateurs.length} validateur(s)`}
          icon={getStatusIcon(getPhaseStatus(validateurs))}
        />
        <Step
          title="Terminé"
        />
      </Steps>

      <Row gutter={24}>
        <Col span={8}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {getStatusIcon(getPhaseStatus(visiteurs))}
            <Text style={{ fontSize: '12px' }}>
              {getStatusText('Visite', visiteurs)}
            </Text>
          </div>
        </Col>
        <Col span={8}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {getStatusIcon(getPhaseStatus(elaborateurs))}
            <Text style={{ fontSize: '12px' }}>
              {getStatusText('Élaboration', elaborateurs)}
            </Text>
          </div>
        </Col>
        <Col span={8}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {getStatusIcon(getPhaseStatus(validateurs))}
            <Text style={{ fontSize: '12px' }}>
              {getStatusText('Validation', validateurs)}
            </Text>
          </div>
        </Col>
      </Row>
    </Card>
  );
};

export default WorkflowProgress;